import { IClinicListResponseData } from "@/api_framework/api_modals/Clinics";
import {
  IConfigurationCityListResponseData,
  IConfigurationClinicCityListResponseData,
  IConfigurationClinicCopyImagesResponseData,
  IConfigurationClinicDetailsResponseData,
  IConfigurationClinicListResponseData,
  IConfigurationClinicOperationalCapacityResponseData,
  IConfigurationEquipmentListResponseData,
  IConfigurationEquipmentServicesListResponseData,
  IConfigurationL1ServiceListResponseData,
  IConfigurationProviderCategoryResponseData,
  IConfigurationProvidersListResponseData,
  IConfigurationRolesAndPermissionListResponseData,
  IConfigurationServiceDetailResponseData,
  IConfigurationServiceListResponseData,
  IConfigurationStaffCategoryListResponseData,
  IConfigurationStaffCitiesListResponseData,
  IConfigurationStaffDetailsResponseData,
  IConfigurationStaffListResponseData,
  IL1ServiceListResponseData,
  IWarehouseListResponseData,
} from "@/api_framework/api_modals/Configuration";
import { IOrgCityListResponseData, IOrgProviderListResponseData } from "@/api_framework/api_modals/Org";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { IRouteReducerSliceStateInit } from "../slice_interface";
import { ICouponserviceBomResponseData } from "@/app/hooks/api_hooks/coupon/useCouponBomListAPICoupon";

export type ConfigurationStateKeys =
  | "cityList"
  | "staffCategoryList"
  | "providerCategoryList"
  | "clinicProviderList"
  | "permissions"
  | "serviceDetail"
  | "l1ServiceList"
  | "serviceList"
  | "equipmentService"
  | "equipmentList"
  | "staffList"
  | "staffRolesList"
  | "staffDetail"
  | "staffCitiesList"
  | "clinicOperationalCapacityList"
  | "clinicListTable"
  | "clinicDetail"
  | "clinicCopyImagesList"
  | "clinics"
  | "cities"
  | "providers";

export interface IConfigurationListState {
  cityList: IConfigurationCityListResponseData[] | null;
  staffCategoryList: IConfigurationStaffCategoryListResponseData[] | null;
  providerCategoryList: IConfigurationProviderCategoryResponseData[] | null;
  clinicProviderList: IConfigurationProvidersListResponseData[] | null;
  serviceDetail: IConfigurationServiceDetailResponseData | null;
  l1ServiceList: IConfigurationL1ServiceListResponseData[] | null;
  serviceList: IConfigurationServiceListResponseData[] | null;
  equipmentService: IConfigurationEquipmentServicesListResponseData[] | null;
  equipmentList: IConfigurationEquipmentListResponseData[] | null;
  staffList: IConfigurationStaffListResponseData | null;
  staffRolesList: IConfigurationRolesAndPermissionListResponseData[] | null;
  staffDetail: IConfigurationStaffDetailsResponseData | null;
  staffCitiesList: IConfigurationStaffCitiesListResponseData[] | null;
  clinicOperationalCapacityList: IConfigurationClinicOperationalCapacityResponseData[] | null;
  clinicListTable: IConfigurationClinicListResponseData[] | null;
  clinicDetail: IConfigurationClinicDetailsResponseData | null;
  clinicCopyImagesList: IConfigurationClinicCopyImagesResponseData[] | null;
  /* Cities Clinics & Providers */
  clinics?: IClinicListResponseData[] | null;
  cities?: IConfigurationClinicCityListResponseData[] | null;
  providers?: IOrgProviderListResponseData[] | null;
  wareHouseList: IWarehouseListResponseData[] | null;
  serviceSetupL1ServiceList: IL1ServiceListResponseData[] | null;
  bomList: ICouponserviceBomResponseData[] | null;
}

export interface IConfigurationListInitialState extends IRouteReducerSliceStateInit {
  state: IConfigurationListState;
}

const initialState: IConfigurationListInitialState = {
  state: {
    cityList: [],
    staffCategoryList: [],
    providerCategoryList: [],
    clinicProviderList: [],
    serviceDetail: null,
    l1ServiceList: [],
    serviceList: [],
    equipmentService: [],
    equipmentList: [],
    staffList: null,
    staffRolesList: [],
    staffDetail: null,
    staffCitiesList: [],
    clinicOperationalCapacityList: [],
    clinicListTable: [],
    clinicDetail: null,
    clinicCopyImagesList: [],
    /* Clinics and Cities */
    cities: [],
    clinics: [],
    providers: [],
    wareHouseList: [],
    serviceSetupL1ServiceList: [],
    bomList: [],
  },
};

const configurationSlice = createSlice({
  name: "configuration_slice",
  initialState: initialState,
  reducers: {
    setConfOrgCities: (
      state,
      action: PayloadAction<{
        data: IOrgCityListResponseData[];
      }>,
    ) => {
      state.state.cities = action.payload.data;
    },
    setConfOrgProviders: (
      state,
      action: PayloadAction<{
        data: IOrgProviderListResponseData[];
      }>,
    ) => {
      state.state.providers = action.payload.data;
    },
    setConfOrgClinics: (
      state,
      action: PayloadAction<{
        data: IClinicListResponseData[];
      }>,
    ) => {
      state.state.clinics = action.payload.data;
    },
    setCityList: (
      state,
      action: PayloadAction<{
        data: IConfigurationCityListResponseData[];
      }>,
    ) => {
      state.state.cityList = action.payload.data;
    },

    setStaffCategoryList: (
      state,
      action: PayloadAction<{
        data: IConfigurationStaffCategoryListResponseData[];
      }>,
    ) => {
      console.log("SETTING DATA");
      state.state.staffCategoryList = action.payload.data;
    },

    setServiceList: (
      state,
      action: PayloadAction<{
        data: IConfigurationServiceListResponseData[];
      }>,
    ) => {
      state.state.serviceList = action.payload.data;
    },
    setServiceDetail: (
      state,
      action: PayloadAction<{
        data: IConfigurationServiceDetailResponseData;
      }>,
    ) => {
      state.state.serviceDetail = action.payload.data;
    },
    setProviderCategoryList: (
      state,
      action: PayloadAction<{
        data: IConfigurationProviderCategoryResponseData[];
      }>,
    ) => {
      state.state.providerCategoryList = action.payload.data;
    },
    setL1ServiceList: (
      state,
      action: PayloadAction<{
        data: IConfigurationL1ServiceListResponseData[];
      }>,
    ) => {
      state.state.l1ServiceList = action.payload.data;
    },
    setEquipmentService: (
      state,
      action: PayloadAction<{
        data: IConfigurationEquipmentServicesListResponseData[];
      }>,
    ) => {
      state.state.equipmentService = action.payload.data;
    },
    setEquipmentList: (
      state,
      action: PayloadAction<{
        data: IConfigurationEquipmentListResponseData[];
      }>,
    ) => {
      state.state.equipmentList = action.payload.data;
    },

    setClinicCopyImagesList: (
      state,
      action: PayloadAction<{
        data: IConfigurationClinicCopyImagesResponseData[];
      }>,
    ) => {
      state.state.clinicCopyImagesList = action.payload.data;
    },
    setClinicDetail: (
      state,
      action: PayloadAction<{
        data: IConfigurationClinicDetailsResponseData;
      }>,
    ) => {
      state.state.clinicDetail = action.payload.data;
    },

    setClinicListTable: (
      state,
      action: PayloadAction<{
        data: IConfigurationClinicListResponseData[];
      }>,
    ) => {
      state.state.clinicListTable = action.payload.data;
    },
    setClinicOperationalCapacityList: (
      state,
      action: PayloadAction<{
        data: IConfigurationClinicOperationalCapacityResponseData[];
      }>,
    ) => {
      state.state.clinicOperationalCapacityList = action.payload.data;
    },

    setStaffCitiesList: (
      state,
      action: PayloadAction<{
        data: IConfigurationStaffCitiesListResponseData[];
      }>,
    ) => {
      state.state.staffCitiesList = action.payload.data;
    },

    setStaffDetail: (
      state,
      action: PayloadAction<{
        data: IConfigurationStaffDetailsResponseData;
      }>,
    ) => {
      state.state.staffDetail = action.payload.data;
    },

    setStaffList: (
      state,
      action: PayloadAction<{
        data: IConfigurationStaffListResponseData;
      }>,
    ) => {
      state.state.staffList = action.payload.data;
    },
    setClinicProviderList: (
      state,
      action: PayloadAction<{
        data: IConfigurationProvidersListResponseData[];
      }>,
    ) => {
      state.state.clinicProviderList = action.payload.data;
    },

    setStaffRolesList: (
      state,
      action: PayloadAction<{
        data: IConfigurationRolesAndPermissionListResponseData[];
      }>,
    ) => {
      state.state.staffRolesList = action.payload.data;
    },

    setClinicCityList: (
      state,
      action: PayloadAction<{
        data: IConfigurationClinicCityListResponseData[];
      }>,
    ) => {
      state.state.cities = action.payload.data;
    },
    setWareHouseList: (
      state,
      action: PayloadAction<{
        data: IWarehouseListResponseData[];
      }>,
    ) => {
      state.state.wareHouseList = action.payload.data;
    },
    setL1ServiceSetupList: (
      state,
      action: PayloadAction<{
        data: IL1ServiceListResponseData[];
      }>,
    ) => {
      state.state.serviceSetupL1ServiceList = action.payload.data;
    },
    setBomSetupList: (
      state,
      action: PayloadAction<{
        data: ICouponserviceBomResponseData[];
      }>,
    ) => {
      state.state.bomList = action.payload.data;
    },
  },
});

export const {
  setCityList,
  setClinicCityList,
  setClinicCopyImagesList,
  setClinicDetail,
  setClinicListTable,
  setClinicOperationalCapacityList,
  setEquipmentList,
  setEquipmentService,
  setL1ServiceList,
  setProviderCategoryList,
  setServiceDetail,
  setServiceList,
  setStaffCategoryList,
  setStaffCitiesList,
  setStaffDetail,
  setStaffList,
  setStaffRolesList,
  setClinicProviderList,
  setConfOrgCities,
  setConfOrgProviders,
  setConfOrgClinics,
  setWareHouseList,
  setL1ServiceSetupList,
  setBomSetupList,
} = configurationSlice.actions;
export default configurationSlice.reducer;
